import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { InformationCircleIcon, CurrencyDollarIcon, PlusCircleIcon, MinusCircleIcon, PercentBadgeIcon } from '@heroicons/react/24/outline';

interface CreditBalance {
  balance: number; // in cents
  currency: string;
  has_credit: boolean;
}

interface ProrationData {
  amount: number; // in cents
  currency: string;
  proration_date?: number;
}

interface CancellationCredit {
  amount: number; // in cents
  currency: string;
  membership_name: string;
  days_remaining: number;
  days_total: number;
}

interface ProrationItem {
  membership_id: string;
  membership_name: string;
  action: 'add' | 'remove';
  amount: number; // in cents
  type: 'charge' | 'credit' | 'info' | 'discount';
  description: string;
  days_remaining?: number;
  days_total?: number;
  currency: string;
}

interface MultiProrationData {
  prorations: ProrationItem[];
  total_amount: number; // in cents
  existing_credit: number; // in cents
  net_amount: number; // in cents
  currency: string;
  calculation_date: number;
}

interface ProrationSummaryProps {
  creditBalance?: CreditBalance | null;
  prorationData?: ProrationData | null;
  cancellationCredit?: CancellationCredit | null;
  multiProrationData?: MultiProrationData | null;
  newMembershipPrice?: number; // in cents
  isCalculating?: boolean;
  showCreditBalance?: boolean;
  showProration?: boolean;
  showCancellationCredit?: boolean;
  showMultiProration?: boolean;
  /** When 'immediate', copy reflects Option C (charge today, steady renewals). */
  chargeTiming?: 'immediate' | 'next_invoice';
  familyBillingPrimaryName?: string | null;
}

// Info icon with tooltip component
function InfoIconWithTooltip() {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isAbove, setIsAbove] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipHeight = 160; // Approximate height of tooltip
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Position above if there's not enough space below, otherwise below
      const shouldPositionAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;
      
      setIsAbove(shouldPositionAbove);
      setPosition({
        top: shouldPositionAbove 
          ? rect.top + window.scrollY - tooltipHeight - 8
          : rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
  }, [isVisible]);

  return (
    <>
      <div
        ref={iconRef}
        className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5 mr-3 flex-shrink-0"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        <InformationCircleIcon className="h-5 w-5 cursor-help" />
      </div>
      {isVisible &&
        createPortal(
          <div
            className="fixed z-50 w-80 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg transform -translate-x-1/2"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
          >
            <p className="font-semibold mb-2">Estimated Credit Amounts</p>
            <p className="mb-2">
              Credit amounts shown are <strong>estimates</strong> calculated at the time you check "Cancel immediately." The actual credit applied by Stripe may differ by 1-2 cents due to the time elapsed before you click "Save Changes."
            </p>
            <p className="text-xs text-gray-300">
              The <strong>Account Balance</strong> shown after saving will reflect the accurate credit amount applied by Stripe.
            </p>
            <div 
              className={`absolute w-3 h-3 bg-gray-800 transform rotate-45 left-1/2 -translate-x-1/2 ${
                isAbove ? '-bottom-1.5' : '-top-1.5'
              }`}
            ></div>
          </div>,
          document.body
        )}
    </>
  );
}

export default function ProrationSummary({
  creditBalance,
  prorationData,
  cancellationCredit,
  multiProrationData,
  newMembershipPrice,
  isCalculating = false,
  showCreditBalance = true,
  showProration = true,
  showCancellationCredit = true,
  showMultiProration = true,
  chargeTiming = 'next_invoice',
  familyBillingPrimaryName,
}: ProrationSummaryProps) {
  
  const formatCurrency = (amountInCents: number, currency: string = 'usd'): string => {
    return `$${(amountInCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  };

  const chargeImmediately = chargeTiming === 'immediate';
  const billingPrimaryLabel = familyBillingPrimaryName?.trim() || 'the billing primary';

  const hasAnythingToShow = 
    (showCreditBalance && creditBalance?.has_credit) ||
    (showProration && prorationData) ||
    (showCancellationCredit && cancellationCredit) ||
    (showMultiProration && multiProrationData && multiProrationData.prorations.length > 0);

  if (!hasAnythingToShow && !isCalculating) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Existing Credit Balance */}
      {showCreditBalance && creditBalance?.has_credit && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start">
            <CurrencyDollarIcon className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-1">
                Available Credit
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                You have <strong>{formatCurrency(creditBalance.balance, creditBalance.currency)}</strong> in credits
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                This credit will automatically apply to the next invoice
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Proration for Membership Changes (Upgrade/Downgrade) */}
      {showProration && prorationData && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">
                Proration Available
              </h4>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                {prorationData.amount > 0 ? (
                  <p>
                    <strong>Charge: {formatCurrency(prorationData.amount, prorationData.currency)}</strong>
                    <span className="block text-xs text-blue-600 dark:text-blue-400 mt-1">
                      {chargeImmediately
                        ? `Charged to ${billingPrimaryLabel}'s card when you save (partial period through the next family renewal).`
                        : 'Prorated charge for the remaining days in your current billing cycle'}
                    </span>
                  </p>
                ) : prorationData.amount < 0 ? (
                  <p>
                    <strong>Credit: {formatCurrency(Math.abs(prorationData.amount), prorationData.currency)}</strong>
                    <span className="block text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Credit will be applied to your next invoice
                    </span>
                  </p>
                ) : (
                  <p>No proration (same price)</p>
                )}
              </div>
              {prorationData.proration_date && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Calculated as of {new Date(prorationData.proration_date * 1000).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Credit Preview */}
      {showCancellationCredit && cancellationCredit && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start">
            <CurrencyDollarIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                Cancellation Credit
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                Canceling <strong>{cancellationCredit.membership_name}</strong> immediately will create a credit of{' '}
                <strong>{formatCurrency(cancellationCredit.amount, cancellationCredit.currency)}</strong>
              </p>
              <div className="text-xs text-yellow-600 dark:text-yellow-400 space-y-1">
                <p>
                  • You've used {cancellationCredit.days_total - cancellationCredit.days_remaining} of {cancellationCredit.days_total} days
                </p>
                <p>
                  • Estimated Credit for {cancellationCredit.days_remaining} remaining days
                </p>
                <p>
                  • Credit will apply to your next invoice
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Net Calculation when both credit and new charge exist */}
      {creditBalance?.has_credit && newMembershipPrice && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 mb-2">
                Net Charge Today
              </h4>
              <div className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1">
                <div className="flex justify-between">
                  <span>New membership:</span>
                  <span>{formatCurrency(newMembershipPrice, creditBalance.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Available credit:</span>
                  <span className="text-green-600 dark:text-green-400">
                    -{formatCurrency(creditBalance.balance, creditBalance.currency)}
                  </span>
                </div>
                <div className="border-t border-indigo-200 dark:border-indigo-700 pt-1 mt-1 flex justify-between font-semibold">
                  <span>Total charge:</span>
                  <span>
                    {formatCurrency(
                      Math.max(0, newMembershipPrice - creditBalance.balance),
                      creditBalance.currency
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Membership Proration */}
      {showMultiProration && multiProrationData && multiProrationData.prorations.length > 0 && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <div className="flex items-start">
            <InfoIconWithTooltip />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-200 mb-3">
                Membership Changes Summary
              </h4>
              {chargeImmediately && (
                <p className="text-xs text-purple-700 dark:text-purple-300 mb-3">
                  {familyBillingPrimaryName
                    ? `Partial-period charges are collected today on ${billingPrimaryLabel}'s card. The next family renewal will only include the regular monthly total.`
                    : 'Partial-period charges are collected today. The next billing date will only include the regular monthly total.'}
                </p>
              )}
              
              {/* List each proration item */}
              <div className="space-y-2 mb-3">
                {multiProrationData.prorations.map((item, index) => (
                  <div key={index} className="flex items-start text-sm">
                    {item.type === 'discount' ? (
                      <PercentBadgeIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    ) : item.action === 'add' ? (
                      <PlusCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    ) : (
                      <MinusCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className={item.type === 'discount' ? 'text-green-700 dark:text-green-300 font-medium' : 'text-purple-700 dark:text-purple-300'}>
                          {item.type === 'discount' ? (
                            <strong>{item.membership_name}</strong>
                          ) : (
                            <>{item.action === 'add' ? 'Add' : 'Remove'}: <strong>{item.membership_name}</strong></>
                          )}
                        </span>
                        {item.type !== 'info' && (
                          <span className={item.type === 'credit' || item.type === 'discount' ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-purple-700 dark:text-purple-300'}>
                            {item.type === 'credit' || item.type === 'discount' ? (
                              <>~-{formatCurrency(Math.abs(item.amount), item.currency)}</>
                            ) : (
                              <>+{formatCurrency(Math.abs(item.amount), item.currency)}</>
                            )}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p className={`text-xs mt-0.5 ${
                          item.type === 'info' ? 'text-yellow-600 dark:text-yellow-400' : 
                          item.type === 'discount' ? 'text-green-600 dark:text-green-400' :
                          'text-purple-600 dark:text-purple-400'
                        }`}>
                          {item.type === 'info' && '⚠️ '}{item.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Total calculation */}
              <div className="border-t border-purple-200 dark:border-purple-700 pt-3 space-y-2">
                <div className="flex justify-between text-sm text-purple-700 dark:text-purple-300">
                  <span>Subtotal:</span>
                  <span>~{formatCurrency(multiProrationData.total_amount, multiProrationData.currency)}</span>
                </div>
                
                {multiProrationData.existing_credit > 0 && (
                  <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                    <span>Existing credit:</span>
                    <span>-{formatCurrency(multiProrationData.existing_credit, multiProrationData.currency)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm font-semibold text-purple-800 dark:text-purple-200 pt-2 border-t border-purple-200 dark:border-purple-700">
                  <span>{chargeImmediately ? 'Estimated charge today:' : 'Net charge today:'}</span>
                  <span>~{formatCurrency(multiProrationData.net_amount, multiProrationData.currency)}</span>
                </div>
              </div>

              <p className="text-xs text-purple-600 dark:text-purple-400 mt-3">
                Calculated as of {new Date(multiProrationData.calculation_date * 1000).toLocaleDateString()}
                {chargeImmediately && (
                  <>
                    {' '}
                    · Renewals stay on the family billing date at the monthly recurring total
                    shown in the family billing panel.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isCalculating && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mr-3"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Calculating proration...
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

